import { useState, useEffect, useCallback } from "react";
import { loadData, saveData } from "../utils/storage";

/**
 * Custom hook for managing timeline state with localStorage persistence.
 */
export function useTimeline() {
  const [data, setData] = useState(() => loadData());

  // Persist to localStorage on every change
  useEffect(() => {
    saveData(data);
  }, [data]);

  const timelines = data.timelines;
  const activeTimelineId = data.activeTimelineId;

  const activeTimeline = timelines.find((t) => t.id === activeTimelineId) || timelines[0];
  const events = activeTimeline?.events || [];

  const setTimelines = useCallback((updater) => {
    setData((prev) => {
      const newTimelines = typeof updater === "function" ? updater(prev.timelines) : updater;
      return { ...prev, timelines: newTimelines };
    });
  }, []);

  const setActiveTimelineId = useCallback((id) => {
    setData((prev) => ({ ...prev, activeTimelineId: id }));
  }, []);

  const setEvents = useCallback(
    (updater) => {
      setTimelines((prev) => prev.map((t) => (t.id === activeTimelineId ? { ...t, events: typeof updater === "function" ? updater(t.events) : updater } : t)));
    },
    [activeTimelineId, setTimelines],
  );

  const addEvent = useCallback(
    (event) => {
      setEvents((prev) => [...prev, { ...event, id: event.id || crypto.randomUUID() }]);
    },
    [setEvents],
  );

  const updateEvent = useCallback(
    (eventId, updates) => {
      setEvents((prev) => prev.map((e) => (e.id === eventId ? { ...e, ...updates } : e)));
    },
    [setEvents],
  );

  const deleteEvent = useCallback(
    (eventId) => {
      setEvents((prev) => prev.filter((e) => e.id !== eventId));
    },
    [setEvents],
  );

  const addTimeline = useCallback((name) => {
    const id = crypto.randomUUID();
    setData((prev) => ({
      ...prev,
      timelines: [...prev.timelines, { id, name, events: [] }],
      activeTimelineId: id,
    }));
  }, []);

  const renameTimeline = useCallback((id, name) => {
    const trimmed = name?.trim();
    if (!trimmed) return;
    setData((prev) => ({
      ...prev,
      timelines: prev.timelines.map((t) => (t.id === id ? { ...t, name: trimmed } : t)),
    }));
  }, []);

  const deleteTimeline = useCallback((id) => {
    setData((prev) => {
      const filtered = prev.timelines.filter((t) => t.id !== id);
      const newTimelines = filtered.length ? filtered : [{ id: "default", name: "Untitled", events: [] }];
      const newActiveId = prev.activeTimelineId === id ? newTimelines[0].id : prev.activeTimelineId;
      return { ...prev, timelines: newTimelines, activeTimelineId: newActiveId };
    });
  }, []);

  const orientation = data.orientation || "vertical";

  const setOrientation = useCallback((value) => {
    setData((prev) => ({ ...prev, orientation: value }));
  }, []);

  const importData = useCallback((importedData) => {
    setData({
      timelines: importedData.timelines || [],
      activeTimelineId: importedData.activeTimelineId || importedData.timelines?.[0]?.id || "default",
      orientation: importedData.orientation || "vertical",
    });
  }, []);

  const exportData = useCallback(() => {
    return { timelines, activeTimelineId, orientation };
  }, [timelines, activeTimelineId, orientation]);

  return {
    timelines,
    activeTimelineId,
    activeTimeline,
    events,
    orientation,
    setActiveTimelineId,
    setOrientation,
    addEvent,
    updateEvent,
    deleteEvent,
    addTimeline,
    renameTimeline,
    deleteTimeline,
    importData,
    exportData,
  };
}
